/**
 * Messaging v2 — React Query hooks for the operations API.
 *
 * Cache-invalidation triggers (v2):
 *   - Primary: SSE (`useInboxStream`) — pushes per-event invalidations from
 *     the server with sub-second latency.
 *   - Fallback: polling — much slower than v1, kept only so the inbox still
 *     converges when SSE is unavailable (dev env without the endpoint, JWT
 *     expired, transient network issue). Belt-and-suspenders, not the
 *     primary signal.
 *
 * Polling cadence (v2):
 *   - Thread list:      every 60s   (was 10s)
 *   - Active thread:    every 30s   (was 2s)
 *   - Phone numbers:    on demand only
 */

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { messagingKeys } from '@/lib/queryKeys';
import { useSseConnected } from '@/stores/inbox.store';
import { messagingApi } from './api';
import { useHasPermission } from '@/hooks/usePermissions';
import type {
  ComposeEmailPayload,
  CreateMessagingGroupPayload,
  ListMessagesParams,
  ListPhoneNumbersParams,
  ListThreadsParams,
  OrderNumberPayload,
  SearchAvailableNumbersParams,
  SendMessagePayload,
  UpdatePhoneNumberPayload,
} from './types';

// ─── Native group messaging ─────────────────────────────────────────────────

export function useMessagingGroupOptions(enabled = true) {
  return useApiQuery({
    queryKey: messagingKeys.groupMessagingOptions(),
    queryFn: () => messagingApi.groupMessaging.options(),
    enabled,
    showErrorToast: false,
    staleTime: 30_000,
  });
}

export function useCreateMessagingGroup() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: CreateMessagingGroupPayload) =>
      messagingApi.groupMessaging.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
      void qc.invalidateQueries({
        queryKey: messagingKeys.groupMessagingOptions(),
      });
    },
  });
}

// ─── Polling intervals ────────────────────────────────────────────────────────
//
// These run ONLY when the SSE stream isn't `open`. Server pushes drive
// every cache invalidation while the stream is live, so polling at
// the same time is wasted work (the user reported "polling messages,
// events and threads constantly" on staging while SSE was working).
// The slow intervals stay as a fallback for environments without the
// stream — local dev that hasn't wired the controller, transient
// outages, JWT-expiry reconnect storms. `useSseConnected` flips on
// when the stream transitions to `open` and back off the moment it
// closes / errors / disconnects, so polling resumes within seconds of
// any degradation.

const POLL_THREAD_LIST_MS = 60_000;
const POLL_ACTIVE_THREAD_MS = 30_000;

// Page size for the infinite thread list. The backend `Paginated<T>` response
// carries `{ page, pageSize, total }`, so we page forward until every thread
// is loaded.
const THREAD_PAGE_SIZE = 30;
const MESSAGE_PAGE_SIZE = 50;

// ─── Thread list ──────────────────────────────────────────────────────────────

export function useThreads(params: ListThreadsParams = {}, options?: { enabled?: boolean }) {
  const sseOpen = useSseConnected();
  const enabled = options?.enabled ?? true;
  return useApiQuery({
    queryKey: messagingKeys.threadList(params as Record<string, unknown>),
    queryFn: () => messagingApi.threads.list(params),
    refetchInterval: enabled && !sseOpen ? POLL_THREAD_LIST_MS : false,
    showErrorToast: false,
    enabled,
  });
}

/**
 * Infinite (cursor-less, page-based) variant of {@link useThreads}. The inbox
 * list uses this so operators can scroll past the first page instead of being
 * capped at `pageSize`. Polling refetches every loaded page while the SSE
 * stream is down, matching `useThreads`' freshness fallback.
 */
export function useInfiniteThreads(params: ListThreadsParams = {}) {
  const sseOpen = useSseConnected();
  const pageSize = params.pageSize ?? THREAD_PAGE_SIZE;
  return useInfiniteQuery({
    // Trailing 'infinite' segment keeps this key distinct from `useThreads`'
    // single-page cache while still sitting under `messagingKeys.threads()`,
    // so SSE-driven `invalidateQueries(threads())` still refreshes it.
    queryKey: [...messagingKeys.threadList(params as Record<string, unknown>), 'infinite'],
    queryFn: ({ pageParam }) =>
      messagingApi.threads.list({
        ...params,
        page: typeof pageParam === 'number' ? pageParam : 1,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.pageSize;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    refetchInterval: sseOpen ? false : POLL_THREAD_LIST_MS,
  });
}

export function useThread(id: string | null | undefined) {
  const sseOpen = useSseConnected();
  return useApiQuery({
    queryKey: id ? messagingKeys.thread(id) : [...messagingKeys.threads(), 'detail', 'none'],
    queryFn: () => messagingApi.threads.get(id as string),
    enabled: Boolean(id),
    refetchInterval: id && !sseOpen ? POLL_ACTIVE_THREAD_MS : false,
    showErrorToast: false,
  });
}

export function useThreadMessages(id: string | null | undefined, params: ListMessagesParams = {}) {
  const sseOpen = useSseConnected();
  return useApiQuery({
    queryKey: id
      ? messagingKeys.threadMessages(id, params as Record<string, unknown>)
      : [...messagingKeys.threads(), 'messages', 'none'],
    queryFn: () => messagingApi.threads.listMessages(id as string, params),
    enabled: Boolean(id),
    refetchInterval: id && !sseOpen ? POLL_ACTIVE_THREAD_MS : false,
    showErrorToast: false,
  });
}

/**
 * Opens a conversation on its newest page and loads progressively older pages
 * when the operator scrolls to the top. The API keeps every individual page in
 * chronological order; the consumer reverses page order when flattening.
 */
export function useInfiniteThreadMessages(
  id: string | null | undefined,
  params: ListMessagesParams = {},
) {
  const sseOpen = useSseConnected();
  const pageSize = params.pageSize ?? MESSAGE_PAGE_SIZE;
  const queryParams = { ...params, latestFirst: true, pageSize };
  return useInfiniteQuery({
    queryKey: id
      ? [...messagingKeys.threadMessages(id, queryParams as Record<string, unknown>), 'infinite']
      : [...messagingKeys.threads(), 'messages', 'none', 'infinite'],
    queryFn: ({ pageParam }) =>
      messagingApi.threads.listMessages(id as string, {
        ...queryParams,
        page: typeof pageParam === 'number' ? pageParam : 1,
      }),
    enabled: Boolean(id),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.pageSize;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    refetchInterval: id && !sseOpen ? POLL_ACTIVE_THREAD_MS : false,
  });
}

// ─── Thread mutations ─────────────────────────────────────────────────────────

export function useSendMessage(threadId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: SendMessagePayload) => messagingApi.threads.send(threadId, payload),
    onSuccess: () => {
      // Force an immediate refetch so the optimistic preview reconciles
      // with the persisted message + any echo reply from the mock.
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({
        queryKey: [...messagingKeys.thread(threadId), 'messages'],
      });
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}

/**
 * Send a reply on an email thread via the connected-mailbox endpoint
 * (`POST /messaging/email/accounts/:channelAccountId/messages`). The
 * SMS `useSendMessage` above can't handle this case — the SMS endpoint
 * requires a `phoneNumberId` UUID, and email threads carry an email-
 * sentinel id that fails validation. After the API call lands, every
 * cache that drives the inbox UI is invalidated so the reply bubble
 * shows up alongside any subsequent inbound activity.
 */
export function useSendEmailReply(threadId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({
      channelAccountId,
      payload,
    }: {
      channelAccountId: string;
      payload: ComposeEmailPayload;
    }) => messagingApi.email.compose(channelAccountId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({
        queryKey: [...messagingKeys.thread(threadId), 'messages'],
      });
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}

export function useMarkRead(threadId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: () => messagingApi.threads.markRead(threadId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}

export function useSnoozeThread(threadId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (until: string) => messagingApi.threads.snooze(threadId, until),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}

export function useCloseThread(threadId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (closingNote?: string) => messagingApi.threads.close(threadId, closingNote),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}

export function useReopenThread(threadId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: () => messagingApi.threads.reopen(threadId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}

/** Enable/disable the channel's AI receptionist for this conversation only
 * (operator takeover). `enabled: false` pauses the bot on this thread. */
export function useSetThreadAiEnabled(threadId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (enabled: boolean) => messagingApi.threads.setAiEnabled(threadId, enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}

export function useSetThreadSection(threadId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (section: 'INBOX' | 'OTHER') => messagingApi.threads.setSection(threadId, section),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.inboxes() });
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}

export function useReassignThread(threadId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (input: { toUserId: string; note?: string }) =>
      messagingApi.threads.reassign(threadId, input.toUserId, input.note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}

export function useSetThreadTags(threadId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (tags: string[]) => messagingApi.threads.setTags(threadId, tags),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}

export function useSetThreadRestricted(threadId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (restricted: boolean) => messagingApi.threads.setRestricted(threadId, restricted),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.thread(threadId) });
      void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
    },
  });
}

// ─── Phone numbers ────────────────────────────────────────────────────────────

export function usePhoneNumbers(
  params: ListPhoneNumbersParams = {},
  options?: { enabled?: boolean },
) {
  const canViewMessages = useHasPermission('messages:view');
  const query = useApiQuery({
    queryKey: messagingKeys.phoneNumberList(params as Record<string, unknown>),
    queryFn: () => messagingApi.phoneNumbers.list(params),
    showErrorToast: false,
    enabled: canViewMessages && (options?.enabled ?? true),
  });
  return canViewMessages ? query : { ...query, data: undefined };
}

export function usePhoneNumber(id: string | null | undefined) {
  return useApiQuery({
    queryKey: id
      ? messagingKeys.phoneNumber(id)
      : [...messagingKeys.phoneNumbers(), 'detail', 'none'],
    queryFn: () => messagingApi.phoneNumbers.get(id as string),
    enabled: Boolean(id),
    showErrorToast: false,
  });
}

export function useAvailableNumbers(params: SearchAvailableNumbersParams, enabled = false) {
  return useApiQuery({
    queryKey: messagingKeys.availableNumbers(params as Record<string, unknown>),
    queryFn: () => messagingApi.phoneNumbers.searchAvailable(params),
    enabled,
    showErrorToast: false,
  });
}

export function useOrderPhoneNumber() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: OrderNumberPayload) => messagingApi.phoneNumbers.order(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.phoneNumbers() });
    },
  });
}

export function useUpdatePhoneNumber(id: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: UpdatePhoneNumberPayload) =>
      messagingApi.phoneNumbers.update(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.phoneNumbers() });
      void qc.invalidateQueries({ queryKey: messagingKeys.phoneNumber(id) });
    },
  });
}

export function useReleasePhoneNumber() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (id: string) => messagingApi.phoneNumbers.release(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.phoneNumbers() });
    },
  });
}
